import { type FC, useContext } from 'react';
import classnames from 'classnames';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { GetHelpZendeskOrderTile } from './GetHelpZendeskOrderTile.tsx';
import { GetHelpZendeskContentDivider } from './GetHelpZendeskContentDivider.tsx';

import { GetHelpZendeskContext } from './GetHelpZendeskContext.ts';
import { ACTIONS, STEPS } from './GetHelpZendeskConstants.ts';
import { trackGetHelpZendeskEvent } from './getHelpZendeskTracking.ts';

import { type GetHelpZendeskLatestOrderQuery } from './__generated__/GetHelpZendeskLatestOrderQuery.graphql.ts';

import dibsCss from 'dibs-css';

const GetHelpZendeskLatestOrder: FC = () => {
    const {
        state: { userId, sellerId, isSeller },
        dispatch,
    } = useContext(GetHelpZendeskContext);

    const viewName = isSeller ? 'dealer.all' : 'buyer.allOrders';

    const { viewer } = useLazyLoadQuery<GetHelpZendeskLatestOrderQuery>(
        graphql`
            query GetHelpZendeskLatestOrderQuery(
                $userId: String
                $isLoggedIn: Boolean!
                $sellerId: String!
                $viewName: String
            ) {
                viewer {
                    transactionsPaginatedSearch(
                        userId: $userId
                        sellerId: $sellerId
                        viewName: $viewName
                        first: 2
                        excludeCanceled: true
                        searchTerm: ""
                    ) @include(if: $isLoggedIn) {
                        edges {
                            node {
                                ...GetHelpZendeskOrderTile_transaction
                            }
                        }
                    }
                }
            }
        `,
        {
            isLoggedIn: !!userId || !!sellerId,
            userId,
            sellerId: sellerId || '',
            viewName,
        }
    );

    const transactions = viewer?.transactionsPaginatedSearch?.edges;
    const latestTransaction = transactions?.[0]?.node;

    if (!latestTransaction) {
        return null;
    }

    const showViewMoreOrderLink = transactions?.length > 1;

    return (
        <div className={dibsCss.mtMedium}>
            <GetHelpZendeskContentDivider />
            <div className={dibsCss.mtMedium}>
                <h2
                    className={classnames(
                        dibsCss.mbXsmall,
                        dibsCss.sassyFontBodyMediumHeavy,
                        dibsCss.textIron
                    )}
                >
                    <FormattedMessage
                        id="getHelp.GetHelpZendeskLatestOrder.heading"
                        defaultMessage="Need help with your latest order?"
                    />
                </h2>
                <GetHelpZendeskOrderTile transaction={latestTransaction} />
                {showViewMoreOrderLink && (
                    <div className={dibsCss.mtSmall}>
                        <Link
                            onClick={() => {
                                dispatch({ type: ACTIONS.SET_STEP, step: STEPS.ORDERS_LIST });
                                trackGetHelpZendeskEvent({
                                    stepInteractionName: 'Full order list viewed',
                                    isSeller,
                                });
                            }}
                        >
                            <span className={dibsCss.sassyFontBodyMedium}>
                                <FormattedMessage
                                    id="getHelp.GetHelpZendeskLatestOrder.viewMore"
                                    defaultMessage="View More Orders"
                                />
                            </span>
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};

export { GetHelpZendeskLatestOrder };
